import React from 'react'
import { DateTime } from 'luxon'
import { When } from 'react-if'
import { Image } from '@/atoms/Image'
import { useFormattedRating } from '@/atoms/MetadataBadge/ContentRatingBadge'
import { AsDiv, CaptionMD, CaptionSM, CaptionXS } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { HeroBannerData } from '@/services/RecommendedBannerService/types'
import { BaseHeroBanner } from '@/views/WatchLandingView/RecommendedBannerCarousel/RecommendedBanner/BaseHeroBanner'

type StreamingBannerProps = {
  banner: HeroBannerData
}

export const StreamingBanner: React.FC<StreamingBannerProps> = ({ banner }) => {
  const backdrop =
    banner.project.title?.landscapeBackdrop?.cloudinaryPath || banner.project.discoveryPosterLandscapeCloudinaryPath
  const mobileBackdrop =
    banner.project.title?.portraitBackdrop?.cloudinaryPath || banner.project.discoveryPosterCloudinaryPath

  const releaseYear = banner.project.releaseDate ? DateTime.fromISO(banner.project.releaseDate).year : null
  const formattedRating = useFormattedRating({
    contentRating: banner.project.metadata?.contentRating,
  })
  const logoPath = banner.project.title?.logo?.cloudinaryPath ?? banner.project.logoCloudinaryPath

  const genres = banner.project.metadata?.genres

  return (
    <BaseHeroBanner
      recommendationId={banner.recommendationId}
      actionHref={`${paths.watch.index}/${banner.project.slug}`}
      altText={banner.project.name as string}
      backdrop={backdrop}
      mobileBackdrop={mobileBackdrop}
      infoSection={
        <div className="mx-auto flex h-full w-full flex-col items-start justify-end gap-2 px-4 pb-8 md:ml-[60px] md:w-[410px] md:justify-center md:px-0 md:pb-0 lg:ml-[76px] xl:ml-[104px] se:w-[300px] se:max-w-full">
          <Image
            alt={banner.project.name as string}
            className="mx-auto h-[80px] w-fit sm:h-[150px] md:mx-0 md:h-[120px] xl:h-[150px]"
            src={`${logoPath}.webp`}
            height={150}
            width={270}
          />
          <CaptionXS weight="medium" className="sm:photon-caption-md md:text-bold w-full text-center md:text-left">
            {banner.flavorText}
          </CaptionXS>
          <CaptionMD
            as={AsDiv}
            weight="medium"
            color="core-gray-400"
            className="mb-6 hidden w-full text-center sm:block md:text-left lg:mb-4 xl:mb-6"
          >
            {banner.project.streamingAtLabel}
          </CaptionMD>
          <div className="flex w-full flex-wrap items-center justify-center gap-[6px] md:justify-start">
            <When condition={Boolean(formattedRating)}>
              <CaptionSM
                weight="medium"
                color="core-gray-400"
                className="md:photon-caption-md flex grow-0 items-center justify-center whitespace-nowrap rounded-full border-core-gray-800 bg-core-gray-800 px-3 py-1"
              >
                {formattedRating}
              </CaptionSM>
            </When>
            <When condition={Boolean(releaseYear)}>
              <CaptionSM
                weight="medium"
                className="md:photon-caption-md text-center md:text-left"
                color="core-gray-500"
              >
                {releaseYear}
              </CaptionSM>
              <CaptionSM
                weight="medium"
                className="md:photon-caption-md text-center md:text-left"
                color="core-gray-500"
              >
                •
              </CaptionSM>
            </When>
            {genres?.map((item, index) => {
              return (
                <CaptionSM
                  key={item}
                  weight="medium"
                  className="md:photon-caption-md text-center md:text-left"
                  color="core-gray-500"
                >
                  {item}
                  {index < genres.length - 1 ? ',' : ''}
                </CaptionSM>
              )
            })}
          </div>
        </div>
      }
    />
  )
}
